import React, { useState, useEffect, useRef } from 'react';
import { getToken } from '../utils/storage';
import CustomerLayout from '../components/CustomerLayout';
import axios from 'axios';
import { toast } from 'sonner';
import WYSIWYGEditor from '../components/WYSIWYGEditor';
import ScreensaverSettings from '../components/ScreensaverSettings';
import { Save, RotateCcw, Download, Upload, Trash2, Eye, EyeOff, Plus, Pipette } from 'lucide-react';
import confirm from '../utils/confirm';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerSettings = () => {
  const { t, i18n } = useTranslation();
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [showLanguageUploadModal, setShowLanguageUploadModal] = useState(false);
  const [languageUploadData, setLanguageUploadData] = useState({
    language_code: '',
    language_name: '',
    file: null
  });
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    weather_location: 'Oslo,NO',
    rotation_speed: 5,
    dark_mode: false,
    language: 'no',
    qr_instruction_text: '<p><b>Scan QR-koden for å sende melding til skjermen</b></p><p><i>Redaktør kan fjerne meldinger uten varsel.</i></p>',
    show_article_header: true,
    show_image_banner: false,
    show_organization_name: true,
    show_address: true,
    show_weather: true,
    show_clock: true,
    show_byline: true,
    show_screen_name: true,
    // Portrait-specific settings
    show_organization_name_portrait: true,
    show_address_portrait: true,
    show_weather_portrait: false,
    show_clock_portrait: true,
    show_byline_portrait: true,
    show_screen_name_portrait: true,
    show_article_header_portrait: true,
    // Font size settings - landscape (liggende)
    message_title_font_size: '1.375rem',
    message_content_font_size: '1.375rem',
    article_title_font_size: '1.55rem',
    article_content_font_size: '1.1rem',
    // Font size settings - portrait (stående)
    message_title_font_size_portrait: '1.375rem',
    message_content_font_size_portrait: '1.375rem',
    article_title_font_size_portrait: '1.5rem',
    article_content_font_size_portrait: '1.1rem',
    // Message background colors
    message_bg_type: 'gradient', // 'solid' or 'gradient'
    message_bg_color_solid: 'rgba(255, 255, 255, 0.4)',
    message_bg_color_start: '#364f4b',
    message_bg_color_end: 'rgba(255, 255, 255, 0.44)',
    message_bg_gradient_angle: 160, // Default 160 degrees
    // Regular message text colors
    message_title_color: '#FFFFFF',
    message_text_color: '#FFFFFF',
    message_date_color: '#AAAAAA',
    // Pinned message colors
    pinned_bg_color: '#FFFFFF',
    pinned_title_color: '#429C88',
    pinned_text_color: '#2A2A2A',
    // Message layout columns
    message_columns_landscape: 2,
    message_columns_portrait: 2,
    // Message tags
    message_tags: [],
    moderation_email: '',
    theme_colors: {
      primary_color: '#50E3C2',
      primary_color_dark: '#429C88',
      dark_mode_bg: '#2A2A2A',
      light_mode_bg: '#F3F3F3',
      font_dark: '#2A2A2A',
      font_light: '#FFFFFF',
      pinned_bg: '#50E3C2',
      pinned_font: '#2A2A2A'
    }
  });
  
  const [locationSearch, setLocationSearch] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeoutRef = useRef(null);
  
  const [profanityWords, setProfanityWords] = useState([]);
  const [showProfanityFilter, setShowProfanityFilter] = useState(false);
  const [showTagsSection, setShowTagsSection] = useState(() => {
    // Load saved preference from localStorage
    const saved = localStorage.getItem('showTagsSection');
    return saved === 'true';
  });

  useEffect(() => {
    fetchOrganization();
  }, []);

  useEffect(() => {
    // Close suggestions when clicking outside
    const handleClickOutside = (e) => {
      if (!e.target.closest('.form-group')) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const searchLocations = async (query) => {
    if (!query || query.length < 2) {
      setLocationSuggestions([]);
      return;
    }

    try {
      // Using Nominatim (OpenStreetMap) for location search
      // Manually encode the query to handle Norwegian characters (æ, ø, å)
      // Build URL manually to avoid double-encoding by axios
      const encodedQuery = encodeURIComponent(query);
      const url = `https://nominatim.openstreetmap.org/search?q=${encodedQuery}&format=json&limit=5&countrycodes=no&addressdetails=1`;
      const response = await axios.get(url);

      const suggestions = response.data.map(place => ({
        name: place.display_name,
        lat: parseFloat(place.lat),
        lon: parseFloat(place.lon),
        city: place.address.city || place.address.town || place.address.village || place.address.municipality,
        formatted: `${place.address.city || place.address.town || place.address.village || place.address.municipality || place.name},NO`
      }));

      setLocationSuggestions(suggestions);
    } catch (error) {
      console.error('Failed to search locations:', error);
    }
  };

  const handleLocationSearchChange = (e) => {
    const value = e.target.value;
    setLocationSearch(value);
    setShowSuggestions(true);

    // Debounce search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchLocations(value);
    }, 300);
  };

  const handleLocationSelect = (suggestion) => {
    setFormData({
      ...formData,
      weather_location: suggestion.formatted
    });
    setLocationSearch(suggestion.city || suggestion.name);
    setShowSuggestions(false);
    setLocationSuggestions([]);
  };

  const fetchOrganization = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/organizations/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Merge response data with existing formData to preserve structure
      setFormData(prev => ({
        ...prev,
        ...response.data
      }));
      setProfanityWords(response.data.profanity_words || []);
    } catch (error) {
      console.error('Settings fetch error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(t('customerSettings.errorLoadingSettings'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = getToken();
      
      // Sanitize QR instruction text before saving
      let sanitizedQrText = formData.qr_instruction_text;
      if (sanitizedQrText) {
        const sanitizeResponse = await axios.post(`${API}/sanitize-html`, {
          html: sanitizedQrText
        });
        sanitizedQrText = sanitizeResponse.data.html;
      }
      
      // Only send fields that are in OrganizationUpdate model
      const updateData = {
        name: formData.name,
        address: formData.address,
        weather_location: formData.weather_location,
        rotation_speed: formData.rotation_speed,
        dark_mode: formData.dark_mode,
        language: formData.language,
        theme_colors: formData.theme_colors,
        qr_instruction_text: sanitizedQrText,
        profanity_words: profanityWords,
        moderation_email: formData.moderation_email,
        show_article_header: formData.show_article_header,
        show_image_banner: formData.show_image_banner,
        show_organization_name: formData.show_organization_name,
        show_address: formData.show_address,
        show_weather: formData.show_weather,
        show_clock: formData.show_clock,
        show_byline: formData.show_byline,
        show_screen_name: formData.show_screen_name,
        // Portrait-specific settings
        show_organization_name_portrait: formData.show_organization_name_portrait,
        show_address_portrait: formData.show_address_portrait,
        show_weather_portrait: formData.show_weather_portrait,
        show_clock_portrait: formData.show_clock_portrait,
        show_byline_portrait: formData.show_byline_portrait,
        show_screen_name_portrait: formData.show_screen_name_portrait,
        // Font size settings - landscape
        message_title_font_size: formData.message_title_font_size,
        message_content_font_size: formData.message_content_font_size,
        article_title_font_size: formData.article_title_font_size,
        article_content_font_size: formData.article_content_font_size,
        // Font size settings - portrait
        message_title_font_size_portrait: formData.message_title_font_size_portrait,
        message_content_font_size_portrait: formData.message_content_font_size_portrait,
        article_title_font_size_portrait: formData.article_title_font_size_portrait,
        article_content_font_size_portrait: formData.article_content_font_size_portrait,
        // Message background colors
        message_bg_type: formData.message_bg_type,
        message_bg_color_solid: formData.message_bg_color_solid,
        message_bg_color_start: formData.message_bg_color_start,
        message_bg_color_end: formData.message_bg_color_end,
        message_bg_gradient_angle: formData.message_bg_gradient_angle,
        // Regular message text colors
        message_title_color: formData.message_title_color,
        message_text_color: formData.message_text_color,
        message_date_color: formData.message_date_color,
        // Pinned message colors
        pinned_bg_color: formData.pinned_bg_color,
        pinned_title_color: formData.pinned_title_color,
        pinned_text_color: formData.pinned_text_color,
        // Message layout columns
        message_columns_landscape: formData.message_columns_landscape,
        message_columns_portrait: formData.message_columns_portrait,
        // Message tags
        message_tags: formData.message_tags,
        show_article_header_portrait: formData.show_article_header_portrait
      };
      
      console.log('Saving settings:', {
        fontSize: updateData.message_title_font_size,
        bgType: updateData.message_bg_type,
        bgColorStart: updateData.message_bg_color_start
      });
      
      await axios.put(`${API}/organizations/me`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('customerSettings.settingsSaved'));
    } catch (error) {
      console.error('Save error:', error.response?.data || error.message);
      // Check if this is a demo user restriction error
      if (error.response?.status === 403 && error.response?.data?.detail?.includes('demo-konto')) {
        toast.error(error.response.data.detail, { duration: 8000 });
      } else {
        toast.error(t('customerSettings.errorSavingSettings', { error: error.response?.data?.detail || error.message }));
      }
    }
  };

  const loadDefaultProfanityWords = async () => {
    if (await confirm.warning(t('customerSettings.confirmResetProfanityList'))) {
      try {
        // Fetch default list from backend
        const response = await axios.get(`${API}/profanity-words/default`);
        setProfanityWords(response.data.words);
        toast.success(t('customerSettings.wordsLoadedFromDefault', { count: response.data.words.length }));
      } catch (error) {
        toast.error(t('customerSettings.errorLoadingDefaultList'));
      }
    }
  };

  const handleProfanityWordsChange = (text) => {
    // Parse comma-separated words
    const words = text
      .split(',')
      .map(w => w.trim().toLowerCase())
      .filter(w => w.length > 0);
    setProfanityWords(words);
  };

  const resetColors = () => {
    setFormData({
      ...formData,
      theme_colors: {
        primary_color: '#50E3C2',
        primary_color_dark: '#429C88',
        dark_mode_bg: '#2A2A2A',
        light_mode_bg: '#F3F3F3',
        font_dark: '#2A2A2A',
        font_light: '#FFFFFF',
        pinned_bg: '#50E3C2',
        pinned_font: '#2A2A2A'
      }
    });
    toast.success(t('customerSettings.colorsResetToDefault'));
  };

  return (
    <CustomerLayout>
      <div className="admin-header">
        <h1>{t('customerSettings.title')}</h1>
        <LanguageSwitcher />
      </div>

      <div>
        <style>{`
          .admin-table input[type="text"],
          .admin-table input[type="number"],
          .admin-table input[type="color"],
          .admin-table textarea {
            color: white !important;
          }
          .admin-table select {
            color: black;
          }
          .admin-table label,
          .admin-table h2,
          .admin-table h3,
          .admin-table h4,
          .admin-table p,
          .admin-table span {
            color: white !important;
          }
          .admin-table .form-group label {
            color: white !important;
          }
          /* Override for pinned message preview - use CSS variables for custom colors */
          .pinned-preview-container {
            --pinned-title-color: #429C88;
            --pinned-text-color: #2A2A2A;
          }
          .pinned-preview-container .pinned-title {
            color: var(--pinned-title-color) !important;
          }
          .pinned-preview-container .pinned-text {
            color: var(--pinned-text-color) !important;
          }
          .pinned-preview-container .pinned-badge {
            color: #FFFFFF !important;
          }
        `}</style>
        <form onSubmit={handleSubmit}>
          <div className="admin-table" style={{ marginBottom: '2rem', padding: '2rem' }}>
            <h2 style={{ color: '#FFFFFF', marginBottom: '1.5rem', fontSize: '1.25rem', fontWeight: 'bold' }}>{t('customerSettings.organizationInfo')}</h2>
            
            <div className="form-group">
              <label>{t('customerSettings.organizationName')}</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                style={{ color: 'white' }}
              />
            </div>

            <div className="form-group">
              <label>{t('customerSettings.address')}</label>
              <input
                type="text"
                value={formData.address || ''}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                style={{ color: 'white' }}
              />
            </div>

            <div className="form-group" style={{ position: 'relative' }}>
              <label>{t('customerSettings.weatherLocation')}</label>
              <input
                type="text"
                value={locationSearch || formData.weather_location}
                onChange={handleLocationSearchChange}
                onFocus={() => setShowSuggestions(true)}
                placeholder={t('customerSettings.searchPlaceholder')}
                autoComplete="off"
              />
              {showSuggestions && locationSuggestions.length > 0 && (
                <div className="location-suggestions">
                  {locationSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className="location-suggestion-item"
                      onClick={() => handleLocationSelect(suggestion)}
                    >
                      {suggestion.name}
                    </div>
                  ))}
                </div>
              )}
              <small style={{ display: 'block', marginTop: '0.5rem', color: '#999' }}>
                {t('customerSettings.selected')}: {formData.weather_location}
              </small>
            </div>

            <div className="form-group">
              <label>{t('customerSettings.rotationSpeed')}</label>
              <input
                type="number"
                min="3"
                max="30"
                value={formData.rotation_speed}
                onChange={(e) => setFormData({...formData, rotation_speed: parseInt(e.target.value)})}
                style={{ color: 'white' }}
              />
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={formData.dark_mode}
                  onChange={(e) => setFormData({...formData, dark_mode: e.target.checked})}
                  style={{ width: 'auto' }}
                />
                {t('customerSettings.darkMode')}
              </label>
            </div>

            {/* Language selection is now in the top-right corner via LanguageSwitcher component */}

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={formData.show_image_banner !== false}
                  onChange={(e) => setFormData({...formData, show_image_banner: e.target.checked})}
                  style={{ width: 'auto' }}
                />
                {t('customerSettings.showBannerLeft')}
              </label>
            </div>

            {/* Display Settings - Two Columns */}
            <h3 style={{ marginTop: '2rem', marginBottom: '1rem', color: '#FFFFFF', fontSize: '1.25rem', fontWeight: 'bold' }}>{t('customerSettings.displaySettings')}</h3>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '2rem',
              background: 'rgba(80, 227, 194, 0.05)',
              padding: '1.5rem',
              borderRadius: '8px',
              border: '1px solid rgba(80, 227, 194, 0.2)'
            }}>
              {/* Landscape Settings */}
              <div>
                <h4 style={{ 
                  marginBottom: '1rem', 
                  color: '#50E3C2',
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  paddingBottom: '0.5rem',
                  borderBottom: '2px solid #50E3C2'
                }}>
                  {t('customerSettings.landscapeDesign')}
                </h4>
                
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.show_organization_name !== false}
                      onChange={(e) => setFormData({...formData, show_organization_name: e.target.checked})}
                      style={{ width: 'auto' }}
                    />
                    {t('customerSettings.showOrgNameShort')}
                  </label>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.show_address !== false}
                      onChange={(e) => setFormData({...formData, show_address: e.target.checked})}
                      style={{ width: 'auto' }}
                    />
                    {t('customerSettings.showAddressShort')}
                  </label>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.show_weather !== false}
                      onChange={(e) => setFormData({...formData, show_weather: e.target.checked})}
                      style={{ width: 'auto' }}
                    />
                    {t('customerSettings.showWeatherShort')}
                  </label>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.show_clock !== false}
                      onChange={(e) => setFormData({...formData, show_clock: e.target.checked})}
                      style={{ width: 'auto' }}
                    />
                    {t('customerSettings.showClockShort')}
                  </label>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.show_byline !== false}
                      onChange={(e) => setFormData({...formData, show_byline: e.target.checked})}
                      style={{ width: 'auto' }}
                    />
                    {t('customerSettings.showByline')}
                  </label>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.show_screen_name !== false}
                      onChange={(e) => setFormData({...formData, show_screen_name: e.target.checked})}
                      style={{ width: 'auto' }}
                    />
                    {t('customerSettings.showScreenNameShort')}
                  </label>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.show_article_header !== false}
                      onChange={(e) => setFormData({...formData, show_article_header: e.target.checked})}
                      style={{ width: 'auto' }}
                    />
                    {t('customerSettings.showArticleHeader')}
                  </label>
                </div>
              </div>

              {/* Portrait Settings */}
              <div>
                <h4 style={{ 
                  marginBottom: '1rem', 
                  color: '#0075FF',
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  paddingBottom: '0.5rem',
                  borderBottom: '2px solid #0075FF'
                }}>
                  {t('customerSettings.portraitDesign')}
                </h4>
                
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.show_organization_name_portrait !== false}
                      onChange={(e) => setFormData({...formData, show_organization_name_portrait: e.target.checked})}
                      style={{ width: 'auto' }}
                    />
                    {t('customerSettings.showOrgNameShort')}
                  </label>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.show_address_portrait !== false}
                      onChange={(e) => setFormData({...formData, show_address_portrait: e.target.checked})}
                      style={{ width: 'auto' }}
                    />
                    {t('customerSettings.showAddressShort')}
                  </label>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.show_weather_portrait !== false}
                      onChange={(e) => setFormData({...formData, show_weather_portrait: e.target.checked})}
                      style={{ width: 'auto' }}
                    />
                    {t('customerSettings.showWeatherShort')}
                  </label>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.show_clock_portrait !== false}
                      onChange={(e) => setFormData({...formData, show_clock_portrait: e.target.checked})}
                      style={{ width: 'auto' }}
                    />
                    {t('customerSettings.showClockShort')}
                  </label>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.show_byline_portrait !== false}
                      onChange={(e) => setFormData({...formData, show_byline_portrait: e.target.checked})}
                      style={{ width: 'auto' }}
                    />
                    {t('customerSettings.showByline')}
                  </label>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.show_screen_name_portrait !== false}
                      onChange={(e) => setFormData({...formData, show_screen_name_portrait: e.target.checked})}
                      style={{ width: 'auto' }}
                    />
                    {t('customerSettings.showScreenNameShort')}
                  </label>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.show_article_header_portrait !== false}
                      onChange={(e) => setFormData({...formData, show_article_header_portrait: e.target.checked})}
                      style={{ width: 'auto' }}
                    />
                    {t('customerSettings.showArticleHeader')}
                  </label>
                </div>
              </div>
            </div>

            {/* Message Columns Settings */}
            <h3 style={{ marginTop: '2rem', marginBottom: '1rem', color: '#FFFFFF', fontSize: '1.25rem', fontWeight: 'bold' }}>{t('customerSettings.messageColumns')}</h3>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '2rem',
              background: 'rgba(80, 227, 194, 0.05)',
              padding: '1.5rem',
              borderRadius: '8px',
              border: '1px solid rgba(80, 227, 194, 0.2)'
            }}>
              {/* Landscape Column Settings */}
              <div>
                <h4 style={{ 
                  marginBottom: '1rem', 
                  color: '#50E3C2',
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  paddingBottom: '0.5rem',
                  borderBottom: '2px solid #50E3C2'
                }}>
                  {t('customerSettings.landscapeColumns')}
                </h4>
                
                <div className="form-group">
                  <label>{t('customerSettings.messageColumns')}</label>
                  <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                    {[1, 2, 3].map(num => (
                      <label key={num} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="message_columns_landscape"
                          value={num}
                          checked={formData.message_columns_landscape === num}
                          onChange={(e) => setFormData({...formData, message_columns_landscape: parseInt(e.target.value)})}
                          style={{ width: 'auto' }}
                        />
                        {num} {num > 1 ? t('customerSettings.columnPlural') : t('customerSettings.columnSingular')}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Portrait Column Settings */}
              <div>
                <h4 style={{ 
                  marginBottom: '1rem', 
                  color: '#0075FF',
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  paddingBottom: '0.5rem',
                  borderBottom: '2px solid #0075FF'
                }}>
                  {t('customerSettings.portraitDesign')}
                </h4>
                
                <div className="form-group">
                  <label>{t('customerSettings.messageColumnsLabel')}</label>
                  <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                    {[1, 2, 3].map(num => (
                      <label key={num} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="message_columns_portrait"
                          value={num}
                          checked={formData.message_columns_portrait === num}
                          onChange={(e) => setFormData({...formData, message_columns_portrait: parseInt(e.target.value)})}
                          style={{ width: 'auto' }}
                        />
                        {num} {num > 1 ? t('customerSettings.columnPlural') : t('customerSettings.columnSingular')}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group" style={{ marginTop: '2rem' }}>
              <label style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#FFFFFF' }}>{t('customerSettings.qrInstructionText')}</label>
              <WYSIWYGEditor
                key={formData._id || 'editor'}
                value={formData.qr_instruction_text || ''}
                onChange={(html) => setFormData({...formData, qr_instruction_text: html})}
                placeholder={t('customerSettings.qrPlaceholder')}
                minHeight="120px"
              />
              <small style={{ color: '#a0a0a0', marginTop: '0.5rem', display: 'block' }}>
                {t('customerSettings.qrHelpText')}
              </small>
            </div>

            <div className="form-group" style={{ marginTop: '2rem' }}>
              <label style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#FFFFFF' }}>{t('customerSettings.messageLimitations')}</label>
              <div className="form-group" style={{ marginTop: '1rem' }}>
                <label>{t('customerSettings.maxMessageChars')}</label>
                <input
                  type="number"
                  min="50"
                  max="500"
                  value={formData.max_message_chars || 200}
                  onChange={(e) => setFormData({...formData, max_message_chars: parseInt(e.target.value) || 200})}
                  style={{ width: '150px' }}
                />
                <small style={{ color: '#a0a0a0', marginTop: '0.5rem', display: 'block' }}>
                  {t('customerSettings.maxCharsHelpText')}
                </small>
              </div>
            </div>
          </div>

          <div className="admin-table" style={{ marginBottom: '2rem', padding: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ color: '#FFFFFF', margin: 0, fontSize: '1.25rem', fontWeight: 'bold' }}>{t('customerSettings.contentFilter')}</h2>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setShowProfanityFilter(!showProfanityFilter)}
                style={{ fontSize: '0.9rem' }}
              >
                {showProfanityFilter ? t('customerSettings.hideWordList') : t('customerSettings.showWordList')}
              </button>
            </div>
            <p style={{ color: '#a0a0a0', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              {t('customerSettings.contentFilterDescription')}
            </p>
            
            {/* Moderation Email - Always Visible */}
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label style={{ color: 'white' }}>
                {t('customerSettings.moderationEmail', 'E-post for moderering')}
                <small style={{ display: 'block', color: '#a0a0a0', marginTop: '0.25rem' }}>
                  {t('customerSettings.moderationEmailHelp', 'Motta varsler når meldinger må godkjennes')}
                </small>
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
                <input
                  type="email"
                  value={formData.moderation_email || ''}
                  onChange={(e) => setFormData({...formData, moderation_email: e.target.value})}
                  placeholder="moderator@example.com"
                  style={{ 
                    flex: 1,
                    color: 'white',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    padding: '0.5rem',
                    borderRadius: '4px'
                  }}
                />
                <button
                  type="button"
                  onClick={async (e) => {
                    e.preventDefault();
                    try {
                      const token = getToken();
                      const response = await axios.put(`${API}/organizations/me`, {
                        moderation_email: formData.moderation_email
                      }, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      
                      // Update local formData with response to ensure consistency
                      setFormData(prev => ({
                        ...prev,
                        moderation_email: response.data.moderation_email || formData.moderation_email
                      }));
                      
                      toast.success('E-post adresse lagret!');
                    } catch (error) {
                      console.error('Save error:', error);
                      console.error('Error details:', error.response?.data);
                      toast.error('Kunne ikke lagre e-post adresse');
                    }
                  }}
                  className="btn btn-primary"
                  style={{ 
                    whiteSpace: 'nowrap',
                    padding: '0.5rem 1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Save size={16} /> Lagre
                </button>
              </div>
            </div>
            
            {showProfanityFilter && (
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                  <button type="button" className="btn btn-secondary" onClick={loadDefaultProfanityWords}>
                    {t('customerSettings.loadDefaultWordList')}
                  </button>
                </div>
                
                <textarea
                  value={profanityWords.join(', ')}
                  onChange={(e) => handleProfanityWordsChange(e.target.value)}
                  rows={6}
                  placeholder={t('customerSettings.profanityPlaceholder')}
                  style={{ 
                    width: '100%', 
                    padding: '0.75rem', 
                    borderRadius: '8px', 
                    border: '1px solid rgba(255,255,255,0.1)', 
                    background: 'rgba(255,255,255,0.05)', 
                    color: 'white',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    resize: 'vertical'
                  }}
                />
                
                <small style={{ color: '#a0a0a0', marginTop: '0.75rem', display: 'block' }}>
                  {t('customerSettings.totalWordsInFilter', { count: profanityWords.length })}
                </small>
              </div>
            )}
          </div>

          <div className="admin-table" style={{ marginBottom: '2rem', padding: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ color: '#FFFFFF', margin: 0, fontSize: '1.25rem', fontWeight: 'bold' }}>{t('customerSettings.colorSelection')}</h2>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="button" className="btn btn-secondary" onClick={resetColors}>
                  <RotateCcw size={16} /> {t('customerSettings.reset')}
                </button>
                <button type="submit" className="btn btn-primary">
                  <Save size={16} /> Lagre
                </button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              <div className="form-group">
                <label>{t('customerSettings.mainColor')}</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <label style={{ 
                    width: '60px', 
                    height: '40px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: formData.theme_colors?.primary_color || '#50E3C2',
                    border: '1px solid #555',
                    position: 'relative'
                  }}>
                    <Pipette size={18} style={{ color: '#fff' }} />
                    <input
                      type="color"
                      value={formData.theme_colors?.primary_color || '#50E3C2'}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme_colors: {...formData.theme_colors, primary_color: e.target.value}
                      })}
                      style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                    />
                  </label>
                  <input
                    type="text"
                    value={formData.theme_colors?.primary_color || '#50E3C2'}
                    onChange={(e) => setFormData({
                      ...formData,
                      theme_colors: {...formData.theme_colors, primary_color: e.target.value}
                    })}
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('customerSettings.mainColorDark')}</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <label style={{ 
                    width: '60px', 
                    height: '40px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: formData.theme_colors?.primary_color_dark || '#429C88',
                    border: '1px solid #555',
                    position: 'relative'
                  }}>
                    <Pipette size={18} style={{ color: '#fff' }} />
                    <input
                      type="color"
                      value={formData.theme_colors?.primary_color_dark || '#429C88'}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme_colors: {...formData.theme_colors, primary_color_dark: e.target.value}
                      })}
                      style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                    />
                  </label>
                  <input
                    type="text"
                    value={formData.theme_colors?.primary_color_dark || '#429C88'}
                    onChange={(e) => setFormData({
                      ...formData,
                      theme_colors: {...formData.theme_colors, primary_color_dark: e.target.value}
                    })}
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('customerSettings.darkModeBackground')}</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <label style={{ 
                    width: '60px', 
                    height: '40px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: formData.theme_colors?.dark_mode_bg || '#2A2A2A',
                    border: '1px solid #555',
                    position: 'relative'
                  }}>
                    <Pipette size={18} style={{ color: '#fff' }} />
                    <input
                      type="color"
                      value={formData.theme_colors?.dark_mode_bg || '#2A2A2A'}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme_colors: {...formData.theme_colors, dark_mode_bg: e.target.value}
                      })}
                      style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                    />
                  </label>
                  <input
                    type="text"
                    value={formData.theme_colors?.dark_mode_bg || '#2A2A2A'}
                    onChange={(e) => setFormData({
                      ...formData,
                      theme_colors: {...formData.theme_colors, dark_mode_bg: e.target.value}
                    })}
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('customerSettings.lightModeBackground')}</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <label style={{ 
                    width: '60px', 
                    height: '40px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: formData.theme_colors?.light_mode_bg || '#F3F3F3',
                    border: '1px solid #555',
                    position: 'relative'
                  }}>
                    <Pipette size={18} style={{ color: '#333' }} />
                    <input
                      type="color"
                      value={formData.theme_colors?.light_mode_bg || '#F3F3F3'}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme_colors: {...formData.theme_colors, light_mode_bg: e.target.value}
                      })}
                      style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                    />
                  </label>
                  <input
                    type="text"
                    value={formData.theme_colors?.light_mode_bg || '#F3F3F3'}
                    onChange={(e) => setFormData({
                      ...formData,
                      theme_colors: {...formData.theme_colors, light_mode_bg: e.target.value}
                    })}
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('customerSettings.pinnedMessageBackground')}</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <label style={{ 
                    width: '60px', 
                    height: '40px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: formData.theme_colors?.pinned_bg || '#FFFFFF',
                    border: '1px solid #555',
                    position: 'relative'
                  }}>
                    <Pipette size={18} style={{ color: (formData.theme_colors?.pinned_bg || '#FFFFFF').toLowerCase() === '#ffffff' ? '#333' : '#fff' }} />
                    <input
                      type="color"
                      value={formData.theme_colors?.pinned_bg || '#FFFFFF'}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme_colors: {...formData.theme_colors, pinned_bg: e.target.value}
                      })}
                      style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                    />
                  </label>
                  <input
                    type="text"
                    value={formData.theme_colors?.pinned_bg || '#FFFFFF'}
                    onChange={(e) => setFormData({
                      ...formData,
                      theme_colors: {...formData.theme_colors, pinned_bg: e.target.value}
                    })}
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('customerSettings.pinnedMessageFontColor')}</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <label style={{ 
                    width: '60px', 
                    height: '40px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: formData.theme_colors?.pinned_font || '#429C88',
                    border: '1px solid #555',
                    position: 'relative'
                  }}>
                    <Pipette size={18} style={{ color: '#fff' }} />
                    <input
                      type="color"
                      value={formData.theme_colors?.pinned_font || '#429C88'}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme_colors: {...formData.theme_colors, pinned_font: e.target.value}
                      })}
                      style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                    />
                  </label>
                  <input
                    type="text"
                    value={formData.theme_colors?.pinned_font || '#429C88'}
                    onChange={(e) => setFormData({
                      ...formData,
                      theme_colors: {...formData.theme_colors, pinned_font: e.target.value}
                    })}
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('customerSettings.messageBackgroundType')}</label>
                <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="message_bg_type"
                      value="solid"
                      checked={formData.message_bg_type === 'solid'}
                      onChange={(e) => setFormData({...formData, message_bg_type: e.target.value})}
                    />
                    <span>{t('customerSettings.solidColor')}</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="message_bg_type"
                      value="gradient"
                      checked={formData.message_bg_type === 'gradient'}
                      onChange={(e) => setFormData({...formData, message_bg_type: e.target.value})}
                    />
                    <span>{t('customerSettings.gradient')}</span>
                  </label>
                </div>

                {formData.message_bg_type === 'solid' ? (
                  <div>
                    <label>{t('customerSettings.backgroundColor')}</label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <label style={{ 
                        width: '50px', 
                        height: '40px', 
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: formData.message_bg_color_solid || 'rgba(255, 255, 255, 0.4)',
                        border: '1px solid #444',
                        position: 'relative'
                      }}>
                        <Pipette size={18} style={{ color: '#333' }} />
                        <input
                          type="color"
                          value={formData.message_bg_color_solid?.startsWith('rgba') ? '#ffffff' : (formData.message_bg_color_solid || '#ffffff')}
                          onChange={(e) => setFormData({...formData, message_bg_color_solid: e.target.value})}
                          style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                        />
                      </label>
                      <input
                        type="text"
                        value={formData.message_bg_color_solid || 'rgba(255, 255, 255, 0.4)'}
                        onChange={(e) => setFormData({...formData, message_bg_color_solid: e.target.value})}
                        placeholder={t('customerSettings.colorPlaceholder')}
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, message_bg_color_solid: 'rgba(255, 255, 255, 0.4)'})}
                        style={{ padding: '0.5rem', background: '#333', border: 'none', color: '#50E3C2', cursor: 'pointer', borderRadius: '4px' }}
                        title={t('customerSettings.resetToStandard')}
                      >
                        <RotateCcw size={16} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label>{t('customerSettings.gradientStartColor')}</label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
                      <label style={{ 
                        width: '50px', 
                        height: '40px', 
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: formData.message_bg_color_start || '#364f4b',
                        border: '1px solid #444',
                        position: 'relative'
                      }}>
                        <Pipette size={18} style={{ color: '#fff' }} />
                        <input
                          type="color"
                          value={formData.message_bg_color_start?.startsWith('rgba') ? '#364f4b' : (formData.message_bg_color_start || '#364f4b')}
                          onChange={(e) => setFormData({...formData, message_bg_color_start: e.target.value})}
                          style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                        />
                      </label>
                      <input
                        type="text"
                        value={formData.message_bg_color_start || '#364f4b'}
                        onChange={(e) => setFormData({...formData, message_bg_color_start: e.target.value})}
                        placeholder={t('customerSettings.gradientPlaceholder')}
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, message_bg_color_start: '#364f4b'})}
                        style={{ padding: '0.5rem', background: '#333', border: 'none', color: '#50E3C2', cursor: 'pointer', borderRadius: '4px' }}
                        title={t('customerSettings.resetToStandard')}
                      >
                        <RotateCcw size={16} />
                      </button>
                    </div>

                    <label>{t('customerSettings.gradientEndColor')}</label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <label style={{ 
                        width: '50px', 
                        height: '40px', 
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: formData.message_bg_color_end || 'rgba(255, 255, 255, 0.44)',
                        border: '1px solid #444',
                        position: 'relative'
                      }}>
                        <Pipette size={18} style={{ color: '#333' }} />
                        <input
                          type="color"
                          value={formData.message_bg_color_end?.startsWith('rgba') ? '#ffffff' : (formData.message_bg_color_end || '#ffffff')}
                          onChange={(e) => setFormData({...formData, message_bg_color_end: e.target.value})}
                          style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                        />
                      </label>
                      <input
                        type="text"
                        value={formData.message_bg_color_end || 'rgba(255, 255, 255, 0.44)'}
                        onChange={(e) => setFormData({...formData, message_bg_color_end: e.target.value})}
                        placeholder={t('customerSettings.colorPlaceholder')}
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, message_bg_color_end: 'rgba(255, 255, 255, 0.44)'})}
                        style={{ padding: '0.5rem', background: '#333', border: 'none', color: '#50E3C2', cursor: 'pointer', borderRadius: '4px' }}
                        title={t('customerSettings.resetToStandard')}
                      >
                        <RotateCcw size={16} />
                      </button>
                    </div>

                    <label>{t('customerSettings.gradientAngle')}</label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
                      <input
                        type="range"
                        min="0"
                        max="360"
                        value={formData.message_bg_gradient_angle || 30}
                        onChange={(e) => setFormData({...formData, message_bg_gradient_angle: parseInt(e.target.value)})}
                        style={{ flex: 1 }}
                      />
                      <input
                        type="number"
                        min="0"
                        max="360"
                        value={formData.message_bg_gradient_angle || 30}
                        onChange={(e) => setFormData({...formData, message_bg_gradient_angle: parseInt(e.target.value) || 30})}
                        style={{ width: '70px', textAlign: 'center' }}
                      />
                      <span style={{ color: '#888' }}>°</span>
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, message_bg_gradient_angle: 160})}
                        style={{ padding: '0.5rem', background: '#333', border: 'none', color: '#50E3C2', cursor: 'pointer', borderRadius: '4px' }}
                        title={t('customerSettings.resetToStandard')}
                      >
                        <RotateCcw size={16} />
                      </button>
                    </div>

                    <div style={{ 
                      marginTop: '1rem', 
                      padding: '2rem', 
                      background: `linear-gradient(${formData.message_bg_gradient_angle || 30}deg, ${formData.message_bg_color_start || '#364f4b'} 0%, ${formData.message_bg_color_end || 'rgba(255, 255, 255, 0.44)'} 100%)`,
                      border: '1px solid #444',
                      borderRadius: '8px',
                      textAlign: 'center',
                      color: '#2A2A2A',
                      fontWeight: 'bold'
                    }}>
                      Gradient preview
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pinned Message Colors Section */}
          <div className="admin-table" style={{ marginBottom: '2rem', padding: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ color: '#ffffff', fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>
                📌 {t('customerSettings.pinnedMessageColors') || 'Festede meldinger'}
              </h2>
              <button
                type="button"
                onClick={() => setFormData({
                  ...formData,
                  pinned_bg_color: '#FFFFFF',
                  pinned_title_color: '#429C88',
                  pinned_text_color: '#2A2A2A'
                })}
                className="btn btn-secondary"
                style={{ 
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  fontSize: '0.875rem'
                }}
              >
                <RotateCcw size={14} /> {t('customerSettings.resetToDefault') || 'Reset til standard'}
              </button>
            </div>
            <p style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              {t('customerSettings.pinnedMessageColorsDescription') || 'Tilpass fargene for festede meldinger som vises øverst på skjermen.'}
            </p>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: '1.5rem',
              background: 'rgba(80, 227, 194, 0.05)',
              padding: '1.5rem',
              borderRadius: '8px',
              border: '1px solid rgba(80, 227, 194, 0.2)'
            }}>
              {/* Background Color */}
              <div className="form-group">
                <label style={{ color: '#50E3C2', fontWeight: 'bold' }}>
                  {t('customerSettings.pinnedBgColor') || 'Bakgrunnsfarge'}
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
                  <label style={{ 
                    width: '50px', 
                    height: '40px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: formData.pinned_bg_color || '#FFFFFF',
                    border: '1px solid #555',
                    position: 'relative'
                  }}>
                    <Pipette size={20} style={{ color: (formData.pinned_bg_color || '#FFFFFF').toLowerCase() === '#ffffff' ? '#333' : '#fff' }} />
                    <input
                      type="color"
                      value={formData.pinned_bg_color || '#FFFFFF'}
                      onChange={(e) => setFormData({...formData, pinned_bg_color: e.target.value})}
                      style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                    />
                  </label>
                  <input
                    type="text"
                    value={formData.pinned_bg_color || '#FFFFFF'}
                    onChange={(e) => setFormData({...formData, pinned_bg_color: e.target.value})}
                    style={{ flex: 1 }}
                    placeholder="#FFFFFF"
                  />
                </div>
                {/* Quick select from profile colors */}
                <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                  {['#FFFFFF', '#F3F3F3', '#2A2A2A', formData.theme_colors?.primary_color, formData.theme_colors?.dark_mode_bg].filter(Boolean).map((color, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setFormData({...formData, pinned_bg_color: color})}
                      style={{
                        width: '24px',
                        height: '24px',
                        background: color,
                        border: formData.pinned_bg_color === color ? '2px solid #50E3C2' : '1px solid #555',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      title={color}
                    />
                  ))}
                </div>
              </div>

              {/* Title Color */}
              <div className="form-group">
                <label style={{ color: '#50E3C2', fontWeight: 'bold' }}>
                  {t('customerSettings.pinnedTitleColor') || 'Tittelfarge'}
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
                  <label style={{ 
                    width: '50px', 
                    height: '40px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: formData.pinned_title_color || '#429C88',
                    border: '1px solid #555',
                    position: 'relative'
                  }}>
                    <Pipette size={20} style={{ color: '#fff' }} />
                    <input
                      type="color"
                      value={formData.pinned_title_color || '#429C88'}
                      onChange={(e) => setFormData({...formData, pinned_title_color: e.target.value})}
                      style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                    />
                  </label>
                  <input
                    type="text"
                    value={formData.pinned_title_color || '#429C88'}
                    onChange={(e) => setFormData({...formData, pinned_title_color: e.target.value})}
                    style={{ flex: 1 }}
                    placeholder="#429C88"
                  />
                </div>
                {/* Quick select from profile colors */}
                <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                  {['#429C88', '#50E3C2', '#2A2A2A', formData.theme_colors?.primary_color, formData.theme_colors?.primary_color_dark].filter(Boolean).map((color, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setFormData({...formData, pinned_title_color: color})}
                      style={{
                        width: '24px',
                        height: '24px',
                        background: color,
                        border: formData.pinned_title_color === color ? '2px solid #50E3C2' : '1px solid #555',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      title={color}
                    />
                  ))}
                </div>
              </div>

              {/* Text Color */}
              <div className="form-group">
                <label style={{ color: '#50E3C2', fontWeight: 'bold' }}>
                  {t('customerSettings.pinnedTextColor') || 'Tekstfarge'}
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
                  <label style={{ 
                    width: '50px', 
                    height: '40px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: formData.pinned_text_color || '#2A2A2A',
                    border: '1px solid #555',
                    position: 'relative'
                  }}>
                    <Pipette size={20} style={{ color: '#fff' }} />
                    <input
                      type="color"
                      value={formData.pinned_text_color || '#2A2A2A'}
                      onChange={(e) => setFormData({...formData, pinned_text_color: e.target.value})}
                      style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                    />
                  </label>
                  <input
                    type="text"
                    value={formData.pinned_text_color || '#2A2A2A'}
                    onChange={(e) => setFormData({...formData, pinned_text_color: e.target.value})}
                    style={{ flex: 1 }}
                    placeholder="#2A2A2A"
                  />
                </div>
                {/* Quick select from profile colors */}
                <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                  {['#2A2A2A', '#FFFFFF', '#555555', formData.theme_colors?.font_dark, formData.theme_colors?.font_light].filter(Boolean).map((color, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setFormData({...formData, pinned_text_color: color})}
                      style={{
                        width: '24px',
                        height: '24px',
                        background: color,
                        border: formData.pinned_text_color === color ? '2px solid #50E3C2' : '1px solid #555',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Preview */}
            <div style={{ marginTop: '1.5rem' }}>
              <label style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'block' }}>
                {t('customerSettings.preview') || 'Forhåndsvisning'}:
              </label>
              <div 
                className="pinned-preview-container"
                style={{ 
                  background: formData.pinned_bg_color || '#FFFFFF',
                  borderBottom: `5px solid ${formData.pinned_title_color || '#429C88'}`,
                  padding: '15px 20px',
                  borderRadius: '8px',
                  maxWidth: '500px',
                  '--pinned-title-color': formData.pinned_title_color || '#429C88',
                  '--pinned-text-color': formData.pinned_text_color || '#2A2A2A'
                }}>
                <span 
                  className="pinned-badge"
                  style={{ 
                    background: '#e74c3c', 
                    padding: '3px 10px', 
                    borderRadius: '4px', 
                    fontSize: '12px',
                    display: 'inline-block',
                    marginBottom: '8px'
                  }}>
                  📌 Festet
                </span>
                <h3 
                  className="pinned-title"
                  style={{ 
                    margin: '0 0 8px 0',
                    fontSize: '1.2rem',
                    fontWeight: 'bold'
                  }}>
                  Eksempel på festet melding
                </h3>
                <p 
                  className="pinned-text"
                  style={{ 
                    margin: '0 0 8px 0',
                    fontSize: '1rem',
                    lineHeight: '1.4'
                  }}>
                  Dette er en forhåndsvisning av hvordan festede meldinger vil se ut på skjermen.
                </p>
                <span 
                  className="pinned-text"
                  style={{ 
                    opacity: 0.7,
                    fontSize: '0.85rem'
                  }}>
                  i dag
                </span>
              </div>
            </div>
          </div>

          {/* Regular Message Colors Section */}
          <div className="admin-table" style={{ marginBottom: '2rem', padding: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ color: '#ffffff', fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>
                💬 {t('customerSettings.regularMessageColors') || 'Vanlige meldinger'}
              </h2>
              <button
                type="button"
                onClick={() => setFormData({
                  ...formData,
                  message_title_color: '#FFFFFF',
                  message_text_color: '#FFFFFF',
                  message_date_color: '#AAAAAA'
                })}
                className="btn btn-secondary"
                style={{ 
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  fontSize: '0.875rem'
                }}
              >
                <RotateCcw size={14} /> {t('customerSettings.resetToDefault') || 'Reset til standard'}
              </button>
            </div>
            <p style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              {t('customerSettings.regularMessageColorsDescription') || 'Tilpass tekstfargene for vanlige meldinger på skjermen.'}
            </p>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: '1.5rem',
              background: 'rgba(80, 227, 194, 0.05)',
              padding: '1.5rem',
              borderRadius: '8px',
              border: '1px solid rgba(80, 227, 194, 0.2)'
            }}>
              {/* Title Color */}
              <div className="form-group">
                <label style={{ color: '#50E3C2', fontWeight: 'bold' }}>
                  {t('customerSettings.messageTitleColor') || 'Tittelfarge'}
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
                  <label style={{ 
                    width: '50px', 
                    height: '40px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: formData.message_title_color || '#FFFFFF',
                    border: '1px solid #555',
                    position: 'relative'
                  }}>
                    <Pipette size={20} style={{ color: (formData.message_title_color || '#FFFFFF').toLowerCase() === '#ffffff' ? '#333' : '#fff' }} />
                    <input
                      type="color"
                      value={formData.message_title_color || '#FFFFFF'}
                      onChange={(e) => setFormData({...formData, message_title_color: e.target.value})}
                      style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                    />
                  </label>
                  <input
                    type="text"
                    value={formData.message_title_color || '#FFFFFF'}
                    onChange={(e) => setFormData({...formData, message_title_color: e.target.value})}
                    style={{ flex: 1 }}
                    placeholder="#FFFFFF"
                  />
                </div>
                {/* Quick select from profile colors */}
                <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                  {['#FFFFFF', '#50E3C2', '#2A2A2A', formData.theme_colors?.font_light, formData.theme_colors?.primary_color].filter(Boolean).map((color, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setFormData({...formData, message_title_color: color})}
                      style={{
                        width: '24px',
                        height: '24px',
                        background: color,
                        border: formData.message_title_color === color ? '2px solid #50E3C2' : '1px solid #555',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      title={color}
                    />
                  ))}
                </div>
              </div>

              {/* Text Color */}
              <div className="form-group">
                <label style={{ color: '#50E3C2', fontWeight: 'bold' }}>
                  {t('customerSettings.messageTextColor') || 'Meldingstekst farge'}
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
                  <label style={{ 
                    width: '50px', 
                    height: '40px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: formData.message_text_color || '#FFFFFF',
                    border: '1px solid #555',
                    position: 'relative'
                  }}>
                    <Pipette size={20} style={{ color: (formData.message_text_color || '#FFFFFF').toLowerCase() === '#ffffff' ? '#333' : '#fff' }} />
                    <input
                      type="color"
                      value={formData.message_text_color || '#FFFFFF'}
                      onChange={(e) => setFormData({...formData, message_text_color: e.target.value})}
                      style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                    />
                  </label>
                  <input
                    type="text"
                    value={formData.message_text_color || '#FFFFFF'}
                    onChange={(e) => setFormData({...formData, message_text_color: e.target.value})}
                    style={{ flex: 1 }}
                    placeholder="#FFFFFF"
                  />
                </div>
                {/* Quick select from profile colors */}
                <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                  {['#FFFFFF', '#CCCCCC', '#2A2A2A', formData.theme_colors?.font_light, formData.theme_colors?.font_dark].filter(Boolean).map((color, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setFormData({...formData, message_text_color: color})}
                      style={{
                        width: '24px',
                        height: '24px',
                        background: color,
                        border: formData.message_text_color === color ? '2px solid #50E3C2' : '1px solid #555',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      title={color}
                    />
                  ))}
                </div>
              </div>

              {/* Date Color */}
              <div className="form-group">
                <label style={{ color: '#50E3C2', fontWeight: 'bold' }}>
                  {t('customerSettings.messageDateColor') || 'Dato farge'}
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
                  <label style={{ 
                    width: '50px', 
                    height: '40px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: formData.message_date_color || '#AAAAAA',
                    border: '1px solid #555',
                    position: 'relative'
                  }}>
                    <Pipette size={20} style={{ color: '#fff' }} />
                    <input
                      type="color"
                      value={formData.message_date_color || '#AAAAAA'}
                      onChange={(e) => setFormData({...formData, message_date_color: e.target.value})}
                      style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                    />
                  </label>
                  <input
                    type="text"
                    value={formData.message_date_color || '#AAAAAA'}
                    onChange={(e) => setFormData({...formData, message_date_color: e.target.value})}
                    style={{ flex: 1 }}
                    placeholder="#AAAAAA"
                  />
                </div>
                {/* Quick select from profile colors */}
                <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                  {['#AAAAAA', '#888888', '#FFFFFF', '#666666', '#50E3C2'].map((color, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setFormData({...formData, message_date_color: color})}
                      style={{
                        width: '24px',
                        height: '24px',
                        background: color,
                        border: formData.message_date_color === color ? '2px solid #50E3C2' : '1px solid #555',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Preview */}
            <div style={{ marginTop: '1.5rem' }}>
              <label style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'block' }}>
                {t('customerSettings.preview') || 'Forhåndsvisning'}:
              </label>
              <div 
                style={{ 
                  background: formData.message_bg_type === 'gradient' 
                    ? `linear-gradient(${formData.message_bg_gradient_angle || 160}deg, ${formData.message_bg_color_start || '#364f4b'} 0%, ${formData.message_bg_color_end || 'rgba(255, 255, 255, 0.44)'} 100%)`
                    : (formData.message_bg_color_solid || 'rgba(255, 255, 255, 0.4)'),
                  borderBottom: `5px solid ${formData.theme_colors?.primary_color || '#50E3C2'}`,
                  padding: '15px 20px',
                  borderRadius: '8px',
                  maxWidth: '500px'
                }}>
                <span 
                  style={{ 
                    background: '#4CAF50', 
                    color: '#fff',
                    padding: '3px 10px', 
                    borderRadius: '4px', 
                    fontSize: '12px',
                    display: 'inline-block',
                    marginBottom: '8px'
                  }}>
                  Notat
                </span>
                <h3 
                  style={{ 
                    margin: '0 0 8px 0',
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    color: formData.message_title_color || '#FFFFFF'
                  }}>
                  Eksempel på vanlig melding
                </h3>
                <p 
                  style={{ 
                    margin: '0 0 8px 0',
                    fontSize: '1rem',
                    lineHeight: '1.4',
                    color: formData.message_text_color || '#FFFFFF'
                  }}>
                  Dette er en forhåndsvisning av hvordan vanlige meldinger vil se ut på skjermen.
                </p>
                <span 
                  style={{ 
                    fontSize: '0.85rem',
                    color: formData.message_date_color || '#AAAAAA'
                  }}>
                  01.01.2025
                </span>
              </div>
            </div>
          </div>

          {/* Font Size Settings Section */}
          <div className="admin-table" style={{ marginBottom: '2rem', padding: '2rem' }}>
            <h2 style={{ color: '#ffffff', fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>{t('customerSettings.fontSizeSettings')}</h2>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '2rem',
              background: 'rgba(80, 227, 194, 0.05)',
              padding: '1.5rem',
              borderRadius: '8px',
              border: '1px solid rgba(80, 227, 194, 0.2)'
            }}>
              {/* Liggende skjermer (Landscape) */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0, color: '#50E3C2', fontSize: '1.25rem', fontWeight: 'bold' }}>{t('customerSettings.landscapeScreens')}</h3>
                  <button
                    type="button"
                    onClick={() => setFormData({
                      ...formData,
                      message_title_font_size: '1.375rem',
                      message_content_font_size: '1.375rem',
                      article_title_font_size: '1.5rem',
                      article_content_font_size: '1.1rem'
                    })}
                    style={{ 
                      padding: '0.4rem 0.75rem', 
                      background: '#333', 
                      border: 'none', 
                      color: '#50E3C2', 
                      cursor: 'pointer', 
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                      fontSize: '0.875rem'
                    }}
                    title={t('customerSettings.resetToStandard')}
                  >
                    <RotateCcw size={14} /> {t('customerSettings.reset')}
                  </button>
                </div>
                
                <div className="form-group">
                  <label>{t('customerSettings.messagesTitle')}</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const val = parseFloat(formData.message_title_font_size || '1.375');
                        setFormData({...formData, message_title_font_size: `${Math.max(0.5, val - 0.125)}rem`});
                      }}
                      style={{ padding: '0.5rem 0.75rem', background: '#333', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: '4px' }}
                    >
                      −
                    </button>
                    <input
                      type="text"
                      value={formData.message_title_font_size || '1.375rem'}
                      onChange={(e) => setFormData({...formData, message_title_font_size: e.target.value})}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const val = parseFloat(formData.message_title_font_size || '1.375');
                        setFormData({...formData, message_title_font_size: `${val + 0.125}rem`});
                      }}
                      style={{ padding: '0.5rem 0.75rem', background: '#333', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: '4px' }}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('customerSettings.messagesContent')}</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const val = parseFloat(formData.message_content_font_size || '1.375');
                        setFormData({...formData, message_content_font_size: `${Math.max(0.5, val - 0.125)}rem`});
                      }}
                      style={{ padding: '0.5rem 0.75rem', background: '#333', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: '4px' }}
                    >
                      −
                    </button>
                    <input
                      type="text"
                      value={formData.message_content_font_size || '1.375rem'}
                      onChange={(e) => setFormData({...formData, message_content_font_size: e.target.value})}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const val = parseFloat(formData.message_content_font_size || '1.375');
                        setFormData({...formData, message_content_font_size: `${val + 0.125}rem`});
                      }}
                      style={{ padding: '0.5rem 0.75rem', background: '#333', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: '4px' }}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('customerSettings.articlesTitle')}</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const val = parseFloat(formData.article_title_font_size || '1.5');
                        setFormData({...formData, article_title_font_size: `${Math.max(0.5, val - 0.125)}rem`});
                      }}
                      style={{ padding: '0.5rem 0.75rem', background: '#333', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: '4px' }}
                    >
                      −
                    </button>
                    <input
                      type="text"
                      value={formData.article_title_font_size || '1.5rem'}
                      onChange={(e) => setFormData({...formData, article_title_font_size: e.target.value})}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const val = parseFloat(formData.article_title_font_size || '1.5');
                        setFormData({...formData, article_title_font_size: `${val + 0.125}rem`});
                      }}
                      style={{ padding: '0.5rem 0.75rem', background: '#333', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: '4px' }}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('customerSettings.articlesContent')}</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const val = parseFloat(formData.article_content_font_size || '1.1');
                        setFormData({...formData, article_content_font_size: `${Math.max(0.5, val - 0.125)}rem`});
                      }}
                      style={{ padding: '0.5rem 0.75rem', background: '#333', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: '4px' }}
                    >
                      −
                    </button>
                    <input
                      type="text"
                      value={formData.article_content_font_size || '1.1rem'}
                      onChange={(e) => setFormData({...formData, article_content_font_size: e.target.value})}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const val = parseFloat(formData.article_content_font_size || '1.1');
                        setFormData({...formData, article_content_font_size: `${val + 0.125}rem`});
                      }}
                      style={{ padding: '0.5rem 0.75rem', background: '#333', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: '4px' }}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Stående skjermer (Portrait) */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0, color: '#0075FF', fontSize: '1.25rem', fontWeight: 'bold' }}>{t('customerSettings.portraitScreens')}</h3>
                  <button
                    type="button"
                    onClick={() => setFormData({
                      ...formData,
                      message_title_font_size_portrait: '1.375rem',
                      message_content_font_size_portrait: '1.375rem',
                      article_title_font_size_portrait: '1.5rem',
                      article_content_font_size_portrait: '1.1rem'
                    })}
                    style={{ 
                      padding: '0.4rem 0.75rem', 
                      background: '#333', 
                      border: 'none', 
                      color: '#50E3C2', 
                      cursor: 'pointer', 
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                      fontSize: '0.875rem'
                    }}
                    title="Tilbakestill til standard"
                  >
                    <RotateCcw size={14} /> Reset
                  </button>
                </div>
                
                <div className="form-group">
                  <label>{t('customerSettings.messagesTitle')}</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const val = parseFloat(formData.message_title_font_size_portrait || '1.375');
                        setFormData({...formData, message_title_font_size_portrait: `${Math.max(0.5, val - 0.125)}rem`});
                      }}
                      style={{ padding: '0.5rem 0.75rem', background: '#333', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: '4px' }}
                    >
                      −
                    </button>
                    <input
                      type="text"
                      value={formData.message_title_font_size_portrait || '1.375rem'}
                      onChange={(e) => setFormData({...formData, message_title_font_size_portrait: e.target.value})}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const val = parseFloat(formData.message_title_font_size_portrait || '1.375');
                        setFormData({...formData, message_title_font_size_portrait: `${val + 0.125}rem`});
                      }}
                      style={{ padding: '0.5rem 0.75rem', background: '#333', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: '4px' }}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('customerSettings.messagesContent')}</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const val = parseFloat(formData.message_content_font_size_portrait || '1.375');
                        setFormData({...formData, message_content_font_size_portrait: `${Math.max(0.5, val - 0.125)}rem`});
                      }}
                      style={{ padding: '0.5rem 0.75rem', background: '#333', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: '4px' }}
                    >
                      −
                    </button>
                    <input
                      type="text"
                      value={formData.message_content_font_size_portrait || '1.375rem'}
                      onChange={(e) => setFormData({...formData, message_content_font_size_portrait: e.target.value})}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const val = parseFloat(formData.message_content_font_size_portrait || '1.375');
                        setFormData({...formData, message_content_font_size_portrait: `${val + 0.125}rem`});
                      }}
                      style={{ padding: '0.5rem 0.75rem', background: '#333', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: '4px' }}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('customerSettings.articlesTitle')}</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const val = parseFloat(formData.article_title_font_size_portrait || '1.5');
                        setFormData({...formData, article_title_font_size_portrait: `${Math.max(0.5, val - 0.125)}rem`});
                      }}
                      style={{ padding: '0.5rem 0.75rem', background: '#333', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: '4px' }}
                    >
                      −
                    </button>
                    <input
                      type="text"
                      value={formData.article_title_font_size_portrait || '1.5rem'}
                      onChange={(e) => setFormData({...formData, article_title_font_size_portrait: e.target.value})}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const val = parseFloat(formData.article_title_font_size_portrait || '1.5');
                        setFormData({...formData, article_title_font_size_portrait: `${val + 0.125}rem`});
                      }}
                      style={{ padding: '0.5rem 0.75rem', background: '#333', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: '4px' }}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('customerSettings.articlesContent')}</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const val = parseFloat(formData.article_content_font_size_portrait || '1.1');
                        setFormData({...formData, article_content_font_size_portrait: `${Math.max(0.5, val - 0.125)}rem`});
                      }}
                      style={{ padding: '0.5rem 0.75rem', background: '#333', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: '4px' }}
                    >
                      −
                    </button>
                    <input
                      type="text"
                      value={formData.article_content_font_size_portrait || '1.1rem'}
                      onChange={(e) => setFormData({...formData, article_content_font_size_portrait: e.target.value})}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const val = parseFloat(formData.article_content_font_size_portrait || '1.1');
                        setFormData({...formData, article_content_font_size_portrait: `${val + 0.125}rem`});
                      }}
                      style={{ padding: '0.5rem 0.75rem', background: '#333', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: '4px' }}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Message Tags Section */}
          <div className="admin-table" style={{ marginBottom: '2rem', padding: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', cursor: 'pointer' }} onClick={() => {
              const newValue = !showTagsSection;
              setShowTagsSection(newValue);
              localStorage.setItem('showTagsSection', newValue.toString());
            }}>
              <h2 style={{ color: '#FFFFFF', margin: 0, fontSize: '1.25rem', fontWeight: 'bold' }}>{t('customerSettings.messageTags')}</h2>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <span style={{ color: '#a0a0a0', fontSize: '0.9rem' }}>{showTagsSection ? t('customerSettings.hideTagList') : t('customerSettings.showTagList')}</span>
                <div style={{ 
                  position: 'relative', 
                  width: '50px', 
                  height: '26px', 
                  background: showTagsSection ? (formData.theme_colors?.primary_color || '#50E3C2') : '#4a4a4a',
                  borderRadius: '13px',
                  transition: 'background 0.3s ease'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '3px',
                    left: showTagsSection ? '26px' : '3px',
                    width: '20px',
                    height: '20px',
                    background: '#FFFFFF',
                    borderRadius: '50%',
                    transition: 'left 0.3s ease'
                  }}></div>
                </div>
              </label>
            </div>
            
            {showTagsSection && (
              <>
                <small style={{ color: '#a0a0a0', marginBottom: '1.5rem', display: 'block' }}>
                  {t('customerSettings.tagsDescription')}
                </small>

                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => {
                    const newTag = { name: 'Ny TAG', color: '#50E3C2', text_color: '#FFFFFF' };
                    setFormData({...formData, message_tags: [...(formData.message_tags || []), newTag]});
                  }}
                  style={{ marginBottom: '1.5rem' }}
                >
                  <Plus size={18} /> {t('customerSettings.addNewTag')}
                </button>

                <div style={{ display: 'grid', gap: '1rem' }}>
              {(formData.message_tags || []).map((tag, index) => (
                <div key={index} style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '2fr 1fr 1fr auto',
                  gap: '1rem',
                  alignItems: 'center',
                  padding: '1rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px'
                }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>{t('customerSettings.tagName')}</label>
                    <input
                      type="text"
                      value={tag.name}
                      onChange={(e) => {
                        const newTags = [...formData.message_tags];
                        newTags[index].name = e.target.value;
                        setFormData({...formData, message_tags: newTags});
                      }}
                      placeholder={t('customerSettings.tagNamePlaceholder')}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label>{t('customerSettings.backgroundColor')}</label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <label style={{ 
                        width: '50px', 
                        height: '40px', 
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: tag.color,
                        border: '1px solid #444',
                        position: 'relative'
                      }}>
                        <Pipette size={16} style={{ color: tag.color?.toLowerCase() === '#ffffff' ? '#333' : '#fff' }} />
                        <input
                          type="color"
                          value={tag.color}
                          onChange={(e) => {
                            const newTags = [...formData.message_tags];
                            newTags[index].color = e.target.value;
                            setFormData({...formData, message_tags: newTags});
                          }}
                          style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                        />
                      </label>
                      <span style={{ fontSize: '0.85rem', color: '#a0a0a0' }}>{tag.color}</span>
                    </div>
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label>{t('customerSettings.fontColor')}</label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <label style={{ 
                        width: '50px', 
                        height: '40px', 
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: tag.text_color,
                        border: '1px solid #444',
                        position: 'relative'
                      }}>
                        <Pipette size={16} style={{ color: tag.text_color?.toLowerCase() === '#ffffff' || tag.text_color?.toLowerCase() === '#fff' ? '#333' : '#fff' }} />
                        <input
                          type="color"
                          value={tag.text_color}
                          onChange={(e) => {
                            const newTags = [...formData.message_tags];
                            newTags[index].text_color = e.target.value;
                            setFormData({...formData, message_tags: newTags});
                          }}
                          style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                        />
                      </label>
                      <span style={{ fontSize: '0.85rem', color: '#a0a0a0' }}>{tag.text_color}</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      const newTags = formData.message_tags.filter((_, i) => i !== index);
                      setFormData({...formData, message_tags: newTags});
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    {t('customerSettings.remove')}
                  </button>
                </div>
              ))}
            </div>

                {(!formData.message_tags || formData.message_tags.length === 0) && (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#a0a0a0' }}>
                    {t('customerSettings.noTagsYet')}
                  </div>
                )}
              </>
            )}
          </div>

          <button type="submit" className="btn btn-primary">
            <Save size={20} /> {t('customerSettings.saveSettings')}
          </button>
        </form>

        {/* Screensaver Settings Section */}
        <div style={{ marginTop: '2rem' }}>
          {formData && (
            <ScreensaverSettings 
              organization={formData} 
              onUpdate={fetchOrganization}
            />
          )}
        </div>
      </div>
    </CustomerLayout>
  );
};

export default CustomerSettings;
